import React, {useEffect, useRef} from 'react';
import {getConfig} from '../../support/Config';

/**
 * Виджет Яндекс SmartCaptcha на публичных формах
 * (`docs/features/2026-08-20_auth-registration-recovery`, FR-22/FR-26).
 *
 * Клиентский ключ приходит из runtime-конфига (`/config.json`). Ключа нет —
 * виджет не рендерится и скрипт не загружается: dev и тесты работают без внешних
 * запросов, а сервер в этом режиме проверку пропускает.
 */

const SCRIPT_SRC = 'https://smartcaptcha.yandexcloud.net/captcha.js';
const SCRIPT_ID = 'yandex-smart-captcha';

interface SmartCaptchaApi {
    render: (container: HTMLElement, params: Record<string, unknown>) => number;
    destroy?: (widgetId: number) => void;
    reset?: (widgetId: number) => void;
}

declare global {
    interface Window {
        smartCaptcha?: SmartCaptchaApi;
    }
}

/** Ключ виджета; пустая строка означает «капча выключена». */
export const captchaSiteKey = (): string => getConfig('captchaClientKey') || '';

/** Включена ли капча — формам нужно знать, ждать ли токен перед отправкой. */
export const isCaptchaEnabled = (): boolean => captchaSiteKey().length > 0;

/** Загружает скрипт виджета один раз на страницу. */
function loadScript(): Promise<void> {
    if (window.smartCaptcha) {
        return Promise.resolve();
    }
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
        return new Promise((resolve, reject) => {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('captcha script failed')));
        });
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('captcha script failed'));
        document.head.appendChild(script);
    });
}

interface SmartCaptchaProps {
    /** Токен успешной проверки; пустая строка — токен сброшен и форму слать нельзя. */
    onToken: (token: string) => void;
}

const SmartCaptcha: React.FC<SmartCaptchaProps> = ({onToken}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<number | null>(null);
    const siteKey = captchaSiteKey();

    useEffect(() => {
        if (!siteKey) {
            return;
        }
        let cancelled = false;

        loadScript()
            .then(() => {
                if (cancelled || !containerRef.current || !window.smartCaptcha) {
                    return;
                }
                widgetIdRef.current = window.smartCaptcha.render(containerRef.current, {
                    sitekey: siteKey,
                    hl: 'ru',
                    callback: (token: string) => onToken(token),
                    // Истёкший или отменённый челлендж обнуляет токен: кнопка снова блокируется.
                    'expired-callback': () => onToken(''),
                });
            })
            .catch(() => {
                // Скрипт не загрузился: токена не будет, форма останется заблокированной,
                // а сервер всё равно отклонит запрос без токена (fail-closed).
                onToken('');
            });

        return () => {
            cancelled = true;
            if (widgetIdRef.current !== null) {
                window.smartCaptcha?.destroy?.(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
        // onToken намеренно не в зависимостях: пересоздание виджета на каждый рендер
        // формы сбрасывало бы уже пройденную проверку.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteKey]);

    if (!siteKey) {
        return null;
    }
    return <div ref={containerRef} data-testid="smart-captcha" className="min-h-[100px]"/>;
};

export default SmartCaptcha;
